import { CheckCircle2, XCircle, Wand2, Pencil, Save, X } from 'lucide-react';

export default function FloorPlanMeasurementsSidebar({
  projectId, measurements, templates,
  handleSaveTemplate, handleApplyTemplate, handleDeleteTemplate,
  deleteMeasurement,
  handleReviewMeasurement,
  handleAcceptAllProposed,
  handleRejectAllProposed,
  startEditingMeasurement,
  editingMeasurement,
  saveEditedPoints,
  cancelEditing,
}) {
  const proposed = measurements.filter(m => m.status === 'proposed');
  const accepted = measurements.filter(m => m.status !== 'proposed');

  // Group proposed by trade
  const proposedByTrade = {};
  for (const m of proposed) {
    const t = m.trade || 'Unassigned';
    if (!proposedByTrade[t]) proposedByTrade[t] = [];
    proposedByTrade[t].push(m);
  }

  return (
    <div className="w-72 border-l border-border bg-card overflow-y-auto shrink-0" data-testid="measurements-sidebar">
      <div className="p-3 border-b border-border">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-[10px] font-heading uppercase tracking-widest text-primary/80">Takeoff Measurements</h3>
          <div className="flex items-center gap-2">
            <button onClick={handleSaveTemplate} data-testid="save-template-btn"
              className="text-[9px] font-mono text-muted-foreground hover:text-foreground">Save Template</button>
            <button onClick={() => {
              window.open(`${process.env.REACT_APP_BACKEND_URL}/api/closeout/projects/${projectId}/floor-plan/takeoff-report?fmt=xlsx`, '_blank');
            }} data-testid="export-takeoff-btn"
              className="text-[9px] font-mono text-primary hover:underline">Export XLSX</button>
          </div>
        </div>
        <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Areas</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500" /> Linear</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-violet-500" /> Counts</span>
        </div>
      </div>

      {/* Templates */}
      {templates.length > 0 && (
        <div className="border-b border-border/50 p-2">
          <span className="text-[9px] font-heading uppercase tracking-widest text-muted-foreground px-1">Templates</span>
          <div className="flex flex-wrap gap-1 mt-1">
            {templates.map(t => (
              <button key={t.id} onClick={() => handleApplyTemplate(t)}
                className="group flex items-center gap-1 px-2 py-0.5 text-[9px] font-mono bg-secondary/50 border border-border rounded-sm hover:bg-primary/10 hover:border-primary/30 transition-colors"
                data-testid={`template-${t.id}`}>
                {t.name}
                <span onClick={(e) => { e.stopPropagation(); handleDeleteTemplate(t.id); }}
                  className="text-muted-foreground/30 hover:text-red-400 opacity-0 group-hover:opacity-100">x</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* AI Proposed Measurements */}
      {proposed.length > 0 && (
        <div className="border-b border-amber-300/30 dark:border-amber-800/30 bg-amber-50/30 dark:bg-amber-950/10" data-testid="proposed-measurements-section">
          <div className="px-3 py-2 flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <Wand2 className="w-3 h-3 text-amber-500" />
              <span className="text-[10px] font-heading uppercase tracking-widest text-amber-600 dark:text-amber-400">
                AI Proposed ({proposed.length})
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => handleAcceptAllProposed()} data-testid="accept-all-proposed-btn"
                className="flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-mono text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 rounded-sm transition-colors">
                <CheckCircle2 className="w-3 h-3" /> All
              </button>
              <button onClick={() => handleRejectAllProposed()} data-testid="reject-all-proposed-btn"
                className="flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-mono text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/30 rounded-sm transition-colors">
                <XCircle className="w-3 h-3" /> All
              </button>
            </div>
          </div>

          {Object.entries(proposedByTrade).map(([trade, items]) => (
            <div key={trade}>
              <div className="px-3 py-1 flex items-center justify-between bg-amber-100/20 dark:bg-amber-900/10">
                <span className="text-[9px] font-heading uppercase tracking-widest text-amber-600/60 dark:text-amber-500/60">{trade}</span>
                <div className="flex items-center gap-1">
                  <button onClick={() => handleAcceptAllProposed(trade)}
                    className="text-[8px] font-mono text-emerald-600 dark:text-emerald-400 hover:underline">accept trade</button>
                  <span className="text-muted-foreground/30">|</span>
                  <button onClick={() => handleRejectAllProposed(trade)}
                    className="text-[8px] font-mono text-rose-600 dark:text-rose-400 hover:underline">reject trade</button>
                </div>
              </div>
              <div className="divide-y divide-amber-200/20 dark:divide-amber-800/20">
                {items.map(m => {
                  const isEditing = editingMeasurement?.id === m.id;
                  return (
                  <div key={m.id} className={`px-3 py-2 group ${isEditing ? 'bg-primary/5 border-l-2 border-primary' : ''}`} data-testid={`proposed-measurement-${m.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5 min-w-0 flex-1">
                        <span className={`w-2 h-2 rounded-full shrink-0 ${m.type === 'area' ? 'bg-emerald-500' : m.type === 'linear' ? 'bg-blue-500' : 'bg-violet-500'}`} />
                        <span className="text-xs text-foreground/80 truncate">{m.label || m.type}</span>
                        <span className="text-[8px] font-mono px-1 py-0 rounded-sm bg-amber-200/50 dark:bg-amber-800/30 text-amber-700 dark:text-amber-400 border border-amber-300/50 dark:border-amber-700/30 shrink-0">
                          AI
                        </span>
                      </div>
                      <div className="flex items-center gap-0.5 shrink-0 ml-1">
                        {isEditing ? (
                          <>
                            <button onClick={saveEditedPoints} data-testid={`save-edit-${m.id}`}
                              title="Save edits"
                              className="p-0.5 text-primary hover:bg-primary/10 rounded-sm transition-colors">
                              <Save className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={cancelEditing} data-testid={`cancel-edit-${m.id}`}
                              title="Cancel"
                              className="p-0.5 text-muted-foreground hover:text-foreground rounded-sm transition-colors">
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </>
                        ) : (
                          <>
                            <button onClick={() => startEditingMeasurement(m)} data-testid={`edit-${m.id}`}
                              title="Edit points"
                              className="p-0.5 text-blue-500 hover:text-blue-400 hover:bg-blue-950/20 rounded-sm transition-colors opacity-0 group-hover:opacity-100">
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => handleReviewMeasurement(m.id, 'accept')} data-testid={`accept-${m.id}`}
                              title="Accept"
                              className="p-0.5 text-emerald-500 hover:text-emerald-400 hover:bg-emerald-950/20 rounded-sm transition-colors">
                              <CheckCircle2 className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => handleReviewMeasurement(m.id, 'reject')} data-testid={`reject-${m.id}`}
                              title="Reject"
                              className="p-0.5 text-rose-500 hover:text-rose-400 hover:bg-rose-950/20 rounded-sm transition-colors">
                              <XCircle className="w-3.5 h-3.5" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 mt-0.5">
                      <span className="text-xs font-mono font-medium text-foreground">{m.real_value} {m.unit}</span>
                      {m.material && <span className="text-[9px] text-muted-foreground">{m.material}</span>}
                    </div>
                    {isEditing && (
                      <p className="text-[9px] text-primary/70 mt-1 italic">Drag points on the canvas to adjust, then Save.</p>
                    )}
                  </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Accepted / Manual Measurements */}
      {accepted.length > 0 ? (
        <div className="divide-y divide-border/30">
          {accepted.map(m => (
            <div key={m.id} className="px-3 py-2 hover:bg-muted/10 group" data-testid={`measurement-${m.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <span className={`w-2 h-2 rounded-full ${m.type === 'area' ? 'bg-emerald-500' : m.type === 'linear' ? 'bg-blue-500' : 'bg-violet-500'}`} />
                  <span className="text-xs text-foreground/80 truncate">{m.label || m.type}</span>
                  {m.carried_forward && (
                    <span className="text-[8px] font-mono text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/20 px-1 rounded-sm border border-amber-200 dark:border-amber-800/40">
                      CARRIED
                    </span>
                  )}
                  {m.status === 'accepted' && (
                    <span className="text-[8px] font-mono text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/20 px-1 rounded-sm border border-emerald-200 dark:border-emerald-800/40">
                      AI
                    </span>
                  )}
                </div>
                <button onClick={() => deleteMeasurement(m.id)}
                  className="text-muted-foreground/50 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity">
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              <div className="flex items-center gap-3 mt-0.5">
                <span className="text-xs font-mono font-medium text-foreground">{m.real_value} {m.unit}</span>
                {m.material && <span className="text-[9px] text-muted-foreground">{m.material}</span>}
                {m.trade && <span className="text-[9px] text-muted-foreground/60">{m.trade}</span>}
              </div>
            </div>
          ))}
        </div>
      ) : proposed.length === 0 ? (
        <div className="p-6 text-center text-[10px] text-muted-foreground">
          No measurements yet. Use Area, Linear, or Count tools — or run AI Takeoff.
        </div>
      ) : null}
    </div>
  );
}
